import { createClientLogger, logClientWarning } from '@/client/logging/client-logger';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { ReviewAnswerTemplateEntity } from '@/entities/review-answer-template.entity';
import { extractApiErrorMessageFromUnknown } from '@/shared/errors/api-error';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

type UseAnswerTemplatesArgs = {
    isShown: boolean;
    accessToken: string;
    value?: string;
    setValue?: Dispatch<SetStateAction<string>>;
    onTemplateApplied?: (templateText: string) => void;
    loadOnShow?: boolean;
};

const DEFAULT_NEW_TEMPLATE_NAME = 'Новый шаблон';
const TEMPLATE_FORM_NAME_REQUIRED = 'Название шаблона не может быть пустым.';
const TEMPLATE_FORM_TEXT_REQUIRED = 'Текст шаблона не может быть пустым.';
const TEMPLATE_FORM_GENERIC_ERROR = 'Не удалось сохранить шаблон';
const TEMPLATE_DELETE_GENERIC_ERROR = 'Не удалось удалить шаблон';
const TEMPLATE_LIST_GENERIC_ERROR = 'Не удалось загрузить шаблоны';

const answerTemplatesLogger = createClientLogger({
    scope: 'answer-templates',
});

const normalizeDraftValue = (value: string): string => value.trim();

const mapUnknownErrorToText = (error: unknown, fallback: string): string => {
    return extractApiErrorMessageFromUnknown(error) ?? fallback;
};

export type AnswerTemplatesBindings = {
    answerTemplates: ReviewAnswerTemplateEntity[];
    isTemplatesLoading: boolean;
    templatesErrorText: string | null;
    isTemplatesMenuOpen: boolean;
    isTemplatesEditorShown: boolean;
    editingTemplateId: string | null;
    templateNameDraft: string;
    templateTextDraft: string;
    templateFormErrorText: string | null;
    isTemplateSaving: boolean;
    isTemplateDeleting: boolean;
    canSaveTemplate: boolean;
    canDeleteTemplate: boolean;
    onToggleTemplatesMenu: () => Promise<void>;
    onCloseTemplatesMenu: () => void;
    onOpenTemplatesEditor: () => Promise<void>;
    onCloseTemplatesEditor: () => void;
    onApplyTemplate: (templateId: string) => void;
    onSelectTemplateForEdit: (templateId: string) => void;
    onStartNewTemplateDraft: () => void;
    onTemplateNameDraftChange: (value: string) => void;
    onTemplateTextDraftChange: (value: string) => void;
    onSaveTemplate: () => Promise<void>;
    onDeleteTemplate: () => Promise<void>;
};

export const useAnswerTemplates = ({
    isShown,
    accessToken,
    value = '',
    setValue,
    onTemplateApplied,
    loadOnShow = false,
}: UseAnswerTemplatesArgs): AnswerTemplatesBindings => {
    const { reviewsApi } = useMarketplaceApi();

    const [answerTemplates, setAnswerTemplates] = useState<ReviewAnswerTemplateEntity[]>([]);
    const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
    const [templatesErrorText, setTemplatesErrorText] = useState<string | null>(null);
    const [isTemplatesMenuOpen, setIsTemplatesMenuOpen] = useState(false);
    const [isTemplatesEditorShown, setIsTemplatesEditorShown] = useState(false);

    const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
    const [templateNameDraft, setTemplateNameDraft] = useState(DEFAULT_NEW_TEMPLATE_NAME);
    const [templateTextDraft, setTemplateTextDraft] = useState('');
    const [templateFormErrorText, setTemplateFormErrorText] = useState<string | null>(null);
    const [isTemplateSaving, setIsTemplateSaving] = useState(false);
    const [isTemplateDeleting, setIsTemplateDeleting] = useState(false);

    const applyTemplateToDraft = useCallback((template: ReviewAnswerTemplateEntity) => {
        setEditingTemplateId(template.id);
        setTemplateNameDraft(template.name);
        setTemplateTextDraft(template.text);
        setTemplateFormErrorText(null);
    }, []);

    const startNewTemplateDraft = useCallback(() => {
        setEditingTemplateId(null);
        setTemplateNameDraft(DEFAULT_NEW_TEMPLATE_NAME);
        setTemplateTextDraft('');
        setTemplateFormErrorText(null);
    }, []);

    const loadAnswerTemplates = useCallback(async (): Promise<ReviewAnswerTemplateEntity[]> => {
        setIsTemplatesLoading(true);
        setTemplatesErrorText(null);

        try {
            const response = await reviewsApi.getAnswerTemplates({ authToken: accessToken });
            setAnswerTemplates(response.data);
            return response.data;
        } catch (error) {
            const message = mapUnknownErrorToText(error, TEMPLATE_LIST_GENERIC_ERROR);
            setTemplatesErrorText(message);
            logClientWarning({
                logger: answerTemplatesLogger,
                message: '[client.answer-templates.list] Failed to load answer templates.',
                error,
                context: {
                    operation: 'answer_templates_list',
                },
            });
            return [];
        } finally {
            setIsTemplatesLoading(false);
        }
    }, [accessToken, reviewsApi]);

    const onToggleTemplatesMenu = useCallback(async () => {
        if (isTemplatesMenuOpen) {
            setIsTemplatesMenuOpen(false);
            return;
        }

        await loadAnswerTemplates();
        setIsTemplatesMenuOpen(true);
    }, [isTemplatesMenuOpen, loadAnswerTemplates]);

    const onOpenTemplatesEditor = useCallback(async () => {
        answerTemplatesLogger.debug('[client.answer-templates.editor] Accepted answer templates editor open request.', {
            operation: 'answer_templates_editor_open',
        });
        await loadAnswerTemplates();
        startNewTemplateDraft();

        setIsTemplatesMenuOpen(false);
        setIsTemplatesEditorShown(true);
    }, [loadAnswerTemplates, startNewTemplateDraft]);

    const onCloseTemplatesEditor = useCallback(() => {
        setIsTemplatesEditorShown(false);
        setTemplateFormErrorText(null);
    }, []);

    const onApplyTemplate = useCallback(
        (templateId: string) => {
            const selectedTemplate = answerTemplates.find((template) => template.id === templateId);
            if (!selectedTemplate || !setValue) {
                return;
            }

            const valueBeforeApply = value;
            setValue((currentValue) => (currentValue === valueBeforeApply ? selectedTemplate.text : currentValue));
            onTemplateApplied?.(selectedTemplate.text);
            setIsTemplatesMenuOpen(false);
        },
        [answerTemplates, onTemplateApplied, setValue, value],
    );

    const onSelectTemplateForEdit = useCallback(
        (templateId: string) => {
            const selectedTemplate = answerTemplates.find((template) => template.id === templateId);
            if (!selectedTemplate) {
                return;
            }

            applyTemplateToDraft(selectedTemplate);
        },
        [answerTemplates, applyTemplateToDraft],
    );

    const onSaveTemplate = useCallback(async () => {
        const normalizedName = normalizeDraftValue(templateNameDraft);
        if (!normalizedName) {
            setTemplateFormErrorText(TEMPLATE_FORM_NAME_REQUIRED);
            return;
        }

        const normalizedText = normalizeDraftValue(templateTextDraft);
        if (!normalizedText) {
            setTemplateFormErrorText(TEMPLATE_FORM_TEXT_REQUIRED);
            return;
        }

        const operation = editingTemplateId ? 'answer_templates_update' : 'answer_templates_create';
        answerTemplatesLogger.info('[client.answer-templates.save] Accepted answer template save request.', {
            operation,
            templateId: editingTemplateId ?? undefined,
        });
        setIsTemplateSaving(true);
        setTemplateFormErrorText(null);

        try {
            const payload = {
                name: normalizedName,
                text: normalizedText,
            };
            const response = editingTemplateId
                ? await reviewsApi.updateAnswerTemplate(editingTemplateId, payload, { authToken: accessToken })
                : await reviewsApi.createAnswerTemplate(payload, { authToken: accessToken });

            const templates = await loadAnswerTemplates();
            const updatedTemplate = templates.find((template) => template.id === response.data.id);

            if (updatedTemplate) {
                applyTemplateToDraft(updatedTemplate);
            } else {
                setEditingTemplateId(response.data.id);
                setTemplateNameDraft(response.data.name);
                setTemplateTextDraft(response.data.text);
            }

            answerTemplatesLogger.info('[client.answer-templates.save] Completed answer template save request.', {
                operation,
                templateId: response.data.id,
            });
        } catch (error) {
            setTemplateFormErrorText(mapUnknownErrorToText(error, TEMPLATE_FORM_GENERIC_ERROR));
            logClientWarning({
                logger: answerTemplatesLogger,
                message: '[client.answer-templates.save] Failed to save answer template.',
                error,
                context: {
                    operation,
                    templateId: editingTemplateId ?? undefined,
                },
            });
        } finally {
            setIsTemplateSaving(false);
        }
    }, [
        accessToken,
        applyTemplateToDraft,
        editingTemplateId,
        loadAnswerTemplates,
        reviewsApi,
        templateNameDraft,
        templateTextDraft,
    ]);

    const onDeleteTemplate = useCallback(async () => {
        if (!editingTemplateId) {
            return;
        }

        answerTemplatesLogger.info('[client.answer-templates.delete] Accepted answer template delete request.', {
            operation: 'answer_templates_delete',
            templateId: editingTemplateId,
        });
        setIsTemplateDeleting(true);
        setTemplateFormErrorText(null);

        try {
            await reviewsApi.deleteAnswerTemplate(editingTemplateId, { authToken: accessToken });

            const templates = await loadAnswerTemplates();
            answerTemplatesLogger.info('[client.answer-templates.delete] Completed answer template delete request.', {
                operation: 'answer_templates_delete',
                templateId: editingTemplateId,
            });

            if (!templates.length) {
                startNewTemplateDraft();
                return;
            }

            applyTemplateToDraft(templates[0]);
        } catch (error) {
            setTemplateFormErrorText(mapUnknownErrorToText(error, TEMPLATE_DELETE_GENERIC_ERROR));
            logClientWarning({
                logger: answerTemplatesLogger,
                message: '[client.answer-templates.delete] Failed to delete answer template.',
                error,
                context: {
                    operation: 'answer_templates_delete',
                    templateId: editingTemplateId,
                },
            });
        } finally {
            setIsTemplateDeleting(false);
        }
    }, [accessToken, applyTemplateToDraft, editingTemplateId, loadAnswerTemplates, reviewsApi, startNewTemplateDraft]);

    useEffect(() => {
        if (!isShown || !loadOnShow) {
            return;
        }

        void loadAnswerTemplates();
    }, [isShown, loadAnswerTemplates, loadOnShow]);

    useEffect(() => {
        if (isShown) {
            return;
        }

        setIsTemplatesMenuOpen(false);
        setIsTemplatesEditorShown(false);
        setTemplateFormErrorText(null);
    }, [isShown]);

    const canSaveTemplate = useMemo(() => {
        // Keep the save action clickable immediately after typing; onSaveTemplate performs
        // the actual validation and avoids an input-state race that can drop fast clicks.
        return !isTemplateSaving && !isTemplateDeleting;
    }, [isTemplateDeleting, isTemplateSaving]);

    const canDeleteTemplate = useMemo(() => {
        return !!editingTemplateId && !isTemplateSaving && !isTemplateDeleting;
    }, [editingTemplateId, isTemplateDeleting, isTemplateSaving]);

    return {
        answerTemplates,
        isTemplatesLoading,
        templatesErrorText,
        isTemplatesMenuOpen,
        isTemplatesEditorShown,
        editingTemplateId,
        templateNameDraft,
        templateTextDraft,
        templateFormErrorText,
        isTemplateSaving,
        isTemplateDeleting,
        canSaveTemplate,
        canDeleteTemplate,
        onToggleTemplatesMenu,
        onCloseTemplatesMenu: () => setIsTemplatesMenuOpen(false),
        onOpenTemplatesEditor,
        onCloseTemplatesEditor,
        onApplyTemplate,
        onSelectTemplateForEdit,
        onStartNewTemplateDraft: startNewTemplateDraft,
        onTemplateNameDraftChange: setTemplateNameDraft,
        onTemplateTextDraftChange: setTemplateTextDraft,
        onSaveTemplate,
        onDeleteTemplate,
    };
};
